import type { CategoryMenuItem } from '~/types/category'

/**
 * Compact, lossless transport of the category list (~1700 categories) that comes with every page.
 *
 * As Pinia state it was ~520 kB of the page's __NUXT_DATA__ (82–95 %), with every field name repeated per
 * category, and the browser revived 1700 objects before hydration: most of the ~800 ms long task on a
 * category page (mobiilinopeus 23.10.2026). Now it is one JSON string of rows, parsed with one JSON.parse.
 *
 * Fields that follow from the others are left out when they do: path (the parent's path + "/" + slug, or
 * "category/" + slug without a parent in the list), level (the parent's level + 1, or 1) and children (the
 * categories whose parentId is this one, in list order). The ~20 categories that differ (custom paths,
 * a parent missing from the list) carry their own value. Absent fields stay absent: a bitmask per row.
 */

const FIELDS = ['id', 'parentId', 'name', 'slug', 'path', 'level', 'children', 'sortOrder', 'popular', 'isActive', 'isIncludedInMenu', 'algoliaFilters', 'image', 'product'] as const
type Field = typeof FIELDS[number]

/** In place of a value that equals the derived one */
const DERIVED = 0
const PATH = FIELDS.indexOf('path')
const LEVEL = FIELDS.indexOf('level')
const CHILDREN = FIELDS.indexOf('children')

export const CATEGORY_CODEC_VERSION = 1

type Category = Partial<CategoryMenuItem> & Pick<CategoryMenuItem, 'id'>

/** Index of each category's parent in the list (undefined: no parent, or a parent missing from the list) */
const parentIndexes = (categories: Category[]) => {
  const indexById = new Map(categories.map((c, i) => [c.id, i]))
  return categories.map(c => (c.parentId != null ? indexById.get(c.parentId) : undefined))
}

/** The categories whose parent is each category, in list order */
const childIds = (categories: Category[], parents: Array<number | undefined>) => {
  const children: number[][] = categories.map(() => [])
  parents.forEach((parent, i) => {
    if (parent !== undefined) children[parent].push(categories[i].id)
  })
  return children
}

/** The rule for path and level, from the parent's actual values */
const derivedPath = (category: Category, parent?: Category) => `${parent ? parent.path : 'category'}/${category.slug}`
const derivedLevel = (parent?: Category) => (parent ? (parent.level ?? 0) + 1 : 1)

const sameIds = (a?: number[], b?: number[]) => !!a && !!b && a.length === b.length && a.every((id, i) => id === b[i])

export function encodeCategories(categories: CategoryMenuItem[]): string {
  const parents = parentIndexes(categories)
  const children = childIds(categories, parents)
  const rows = categories.map((c, i) => {
    const parent = parents[i] !== undefined ? categories[parents[i] as number] : undefined
    let absent = 0
    const row: unknown[] = FIELDS.map((field: Field, f) => {
      if (c[field] === undefined) {
        absent |= 1 << f
        return null
      }
      if (f === PATH && c.path === derivedPath(c, parent)) return DERIVED
      if (f === LEVEL && c.level === derivedLevel(parent)) return DERIVED
      if (f === CHILDREN && sameIds(c.children, children[i])) return DERIVED
      return c[field]
    })
    row.push(absent)
    return row
  })
  return JSON.stringify({ v: CATEGORY_CODEC_VERSION, f: FIELDS, rows })
}

export function decodeCategories(encoded: string): CategoryMenuItem[] {
  const { v, f, rows } = JSON.parse(encoded) as { v: number, f: Field[], rows: unknown[][] }
  if (v !== CATEGORY_CODEC_VERSION || f.join() !== FIELDS.join()) throw new Error(`Unknown category encoding v${v}`)
  const categories: Category[] = rows.map((row) => {
    const absent = row[FIELDS.length] as number
    const category: Record<string, unknown> = {}
    FIELDS.forEach((field, i) => {
      if (!(absent & (1 << i))) category[field] = row[i]
    })
    return category as Category
  })

  const parents = parentIndexes(categories)
  const children = childIds(categories, parents)
  // A parent's path and level are resolved before its children's, which are derived from them
  const resolved = new Set<number>()
  const resolve = (i: number, depth = 0) => {
    if (resolved.has(i) || depth > 64) return
    resolved.add(i)
    const parentIndex = parents[i]
    if (parentIndex !== undefined) resolve(parentIndex, depth + 1)
    const parent = parentIndex !== undefined ? categories[parentIndex] : undefined
    if (rows[i][PATH] === DERIVED) categories[i].path = derivedPath(categories[i], parent)
    if (rows[i][LEVEL] === DERIVED) categories[i].level = derivedLevel(parent)
    if (rows[i][CHILDREN] === DERIVED) categories[i].children = children[i]
  }
  categories.forEach((_, i) => resolve(i))
  return categories as CategoryMenuItem[]
}
